
import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { LogOut } from 'lucide-react';
import LegalFooter from '@/components/LegalFooter';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ROLE_LABELS = { chef_poste: 'Chef de Poste', agent: 'Agent' };
// Ordre d'affichage : chef de poste d'abord, puis agent, puis le reste
const ROLE_ORDER = ['chef_poste', 'agent'];

// Écran intermédiaire affiché après connexion pour les comptes affectés à
// plusieurs sites (et non administrateurs) : un utilisateur peut être chef de
// poste sur un site et seulement agent sur un autre, il doit donc choisir sur
// quel site il travaille pour cette session avant d'accéder à l'application.
const SiteSelector = ({ user, onSiteSelected, onLogout }) => {
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selecting, setSelecting] = useState(null);

  const getRoleForSite = (siteId) => (user?.site_roles || {})[siteId] || user?.role;
  const roleRank = (siteId) => {
    const idx = ROLE_ORDER.indexOf(getRoleForSite(siteId));
    return idx === -1 ? 99 : idx;
  };

  useEffect(() => {
    const loadSites = async () => {
      const token = localStorage.getItem('token');
      try {
        const res = await fetch(`${BACKEND_URL}/api/sites`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.ok) {
          const all = await res.json();
          const assigned = user?.assigned_sites || [];
          const mine = all.filter(s => assigned.includes(s.id));
          // Chef de poste en premier, puis agent, puis le reste ; ordre alphabétique à l'intérieur de chaque groupe
          mine.sort((a, b) => {
            const roleDiff = roleRank(a.id) - roleRank(b.id);
            if (roleDiff !== 0) return roleDiff;
            return a.name.localeCompare(b.name, 'fr');
          });
          setSites(mine);
        }
      } catch { /* silencieux */ }
      finally { setLoading(false); }
    };
    loadSites();
    // eslint-disable-next-line
  }, [user]);

  const handleSelect = async (siteId) => {
    setSelecting(siteId);
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/select-site`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ site_id: siteId })
      });
      if (res.ok) {
        const data = await res.json();
        onSiteSelected(data.access_token, data.user);
      } else {
        toast.error('Impossible de sélectionner ce site');
        setSelecting(null);
      }
    } catch {
      toast.error('Erreur de connexion');
      setSelecting(null);
    }
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-[#0a1628] via-[#1a365d] to-[#2563eb]">
      <div
        className="w-full max-w-[480px] bg-white dark:bg-slate-800 rounded-2xl overflow-hidden p-8"
        style={{
          boxShadow: '0 25px 60px -15px rgba(0, 0, 0, 0.35), 0 10px 30px -10px rgba(0, 0, 0, 0.2), 0 0 40px -5px rgba(59, 130, 246, 0.15)'
        }}
      >
        <img
          src="/logo-action-securite.png"
          alt="Action Sécurité"
          className="h-20 mx-auto mb-5 object-contain"
        />

        <h1 className="text-[#0f172a] text-xl font-semibold tracking-tight mb-2 text-center">
          Choisissez votre site
        </h1>
        <p className="text-[#64748b] text-sm mb-6 text-center">
          Bonjour {user?.name}, vous êtes affecté à plusieurs sites. Votre rôle peut être différent selon le site sur lequel vous travaillez.
        </p>

        {loading ? (
          <div className="text-center py-6">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          </div>
        ) : (
          <div className="space-y-2">
            {sites.map(site => {
              const role = getRoleForSite(site.id);
              const roleLabel = ROLE_LABELS[role] || role;
              return (
                <button
                  key={site.id}
                  type="button"
                  onClick={() => handleSelect(site.id)}
                  disabled={selecting !== null}
                  className="w-full flex items-center justify-between gap-2 text-left px-4 py-3 rounded-xl border border-slate-200 hover:border-blue-400 hover:bg-blue-50 dark:border-slate-600 dark:hover:bg-slate-700 transition-colors disabled:opacity-50"
                  data-testid={`select-site-${site.id}`}
                >
                  <span className="font-medium text-slate-800 dark:text-slate-100">{site.name}</span>
                  <span className="flex items-center gap-2 shrink-0">
                    {roleLabel && (
                      <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${
                        role === 'chef_poste'
                          ? 'bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300'
                          : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300'
                      }`}>
                        {roleLabel}
                      </span>
                    )}
                    {selecting === site.id && <span className="text-xs text-slate-400">Chargement...</span>}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        <button
          onClick={onLogout}
          className="mt-6 inline-flex items-center gap-2 text-sm text-[#2563eb] hover:text-[#1d4ed8] font-medium"
        >
          <LogOut className="h-4 w-4" />
          Se déconnecter
        </button>
      </div>
      <LegalFooter className="absolute bottom-3 inset-x-0 !mt-0 text-blue-200/70" />
    </div>
  );
};

export default SiteSelector;
